/**
 * Curated Solar (Bold) icon set for the per-diagram icon picker (Notion/Linear style).
 * Statically imported so unplugin-icons tree-shakes exactly these into the bundle.
 * The stored value is the KEY string (e.g. "server-2"); render via <DiagramIcon name color/>.
 */
import type { SVGProps, ComponentType } from 'react';
import Ic_document_text from '~icons/solar/document-text-bold';
import Ic_notebook from '~icons/solar/notebook-bold';
import Ic_clipboard_text from '~icons/solar/clipboard-text-bold';
import Ic_folder from '~icons/solar/folder-bold';
import Ic_folder_with_files from '~icons/solar/folder-with-files-bold';
import Ic_widget_2 from '~icons/solar/widget-2-bold';
import Ic_layers_minimalistic from '~icons/solar/layers-minimalistic-bold';
import Ic_box from '~icons/solar/box-bold';
import Ic_diagram_up from '~icons/solar/diagram-up-bold';
import Ic_routing from '~icons/solar/routing-bold';
import Ic_server_2 from '~icons/solar/server-2-bold';
import Ic_database from '~icons/solar/database-bold';
import Ic_cloud from '~icons/solar/cloud-bold';
import Ic_cpu from '~icons/solar/cpu-bold';
import Ic_ssd_square from '~icons/solar/ssd-square-bold';
import Ic_wi_fi_router from '~icons/solar/wi-fi-router-bold';
import Ic_code_square from '~icons/solar/code-square-bold';
import Ic_command from '~icons/solar/command-bold';
import Ic_programming from '~icons/solar/programming-bold';
import Ic_bug from '~icons/solar/bug-bold';
import Ic_home_2 from '~icons/solar/home-2-bold';
import Ic_buildings_2 from '~icons/solar/buildings-2-bold';
import Ic_city from '~icons/solar/city-bold';
import Ic_shop from '~icons/solar/shop-bold';
import Ic_cart_large_2 from '~icons/solar/cart-large-2-bold';
import Ic_wallet from '~icons/solar/wallet-bold';
import Ic_card from '~icons/solar/card-bold';
import Ic_chart_2 from '~icons/solar/chart-2-bold';
import Ic_pie_chart_2 from '~icons/solar/pie-chart-2-bold';
import Ic_graph_up from '~icons/solar/graph-up-bold';
import Ic_user_circle from '~icons/solar/user-circle-bold';
import Ic_users_group_rounded from '~icons/solar/users-group-rounded-bold';
import Ic_chat_round from '~icons/solar/chat-round-bold';
import Ic_bell from '~icons/solar/bell-bold';
import Ic_calendar from '~icons/solar/calendar-bold';
import Ic_clock_circle from '~icons/solar/clock-circle-bold';
import Ic_settings from '~icons/solar/settings-bold';
import Ic_shield_check from '~icons/solar/shield-check-bold';
import Ic_lock from '~icons/solar/lock-bold';
import Ic_key from '~icons/solar/key-bold';
import Ic_global from '~icons/solar/global-bold';
import Ic_map_point from '~icons/solar/map-point-bold';
import Ic_target from '~icons/solar/target-bold';
import Ic_compass from '~icons/solar/compass-bold';
import Ic_rocket from '~icons/solar/rocket-bold';
import Ic_bolt from '~icons/solar/bolt-bold';
import Ic_fire from '~icons/solar/fire-bold';
import Ic_magic_stick_3 from '~icons/solar/magic-stick-3-bold';
import Ic_lightbulb from '~icons/solar/lightbulb-bold';
import Ic_star from '~icons/solar/star-bold';
import Ic_heart from '~icons/solar/heart-bold';
import Ic_bookmark from '~icons/solar/bookmark-bold';
import Ic_flag from '~icons/solar/flag-bold';
import Ic_crown from '~icons/solar/crown-bold';
import Ic_gift from '~icons/solar/gift-bold';
import Ic_palette from '~icons/solar/palette-bold';
import Ic_pen_2 from '~icons/solar/pen-2-bold';
import Ic_atom from '~icons/solar/atom-bold';
import Ic_smartphone from '~icons/solar/smartphone-bold';
import Ic_monitor from '~icons/solar/monitor-bold';
import Ic_camera from '~icons/solar/camera-bold';
import Ic_music_note from '~icons/solar/music-note-bold';

type IconComp = ComponentType<SVGProps<SVGSVGElement>>;

export const DIAGRAM_ICON_MAP: Record<string, IconComp> = {
  'document-text': Ic_document_text,
  'notebook': Ic_notebook,
  'clipboard-text': Ic_clipboard_text,
  'folder': Ic_folder,
  'folder-with-files': Ic_folder_with_files,
  'widget-2': Ic_widget_2,
  'layers-minimalistic': Ic_layers_minimalistic,
  'box': Ic_box,
  'diagram-up': Ic_diagram_up,
  'routing': Ic_routing,
  'server-2': Ic_server_2,
  'database': Ic_database,
  'cloud': Ic_cloud,
  'cpu': Ic_cpu,
  'ssd-square': Ic_ssd_square,
  'wi-fi-router': Ic_wi_fi_router,
  'code-square': Ic_code_square,
  'command': Ic_command,
  'programming': Ic_programming,
  'bug': Ic_bug,
  'home-2': Ic_home_2,
  'buildings-2': Ic_buildings_2,
  'city': Ic_city,
  'shop': Ic_shop,
  'cart-large-2': Ic_cart_large_2,
  'wallet': Ic_wallet,
  'card': Ic_card,
  'chart-2': Ic_chart_2,
  'pie-chart-2': Ic_pie_chart_2,
  'graph-up': Ic_graph_up,
  'user-circle': Ic_user_circle,
  'users-group-rounded': Ic_users_group_rounded,
  'chat-round': Ic_chat_round,
  'bell': Ic_bell,
  'calendar': Ic_calendar,
  'clock-circle': Ic_clock_circle,
  'settings': Ic_settings,
  'shield-check': Ic_shield_check,
  'lock': Ic_lock,
  'key': Ic_key,
  'global': Ic_global,
  'map-point': Ic_map_point,
  'target': Ic_target,
  'compass': Ic_compass,
  'rocket': Ic_rocket,
  'bolt': Ic_bolt,
  'fire': Ic_fire,
  'magic-stick-3': Ic_magic_stick_3,
  'lightbulb': Ic_lightbulb,
  'star': Ic_star,
  'heart': Ic_heart,
  'bookmark': Ic_bookmark,
  'flag': Ic_flag,
  'crown': Ic_crown,
  'gift': Ic_gift,
  'palette': Ic_palette,
  'pen-2': Ic_pen_2,
  'atom': Ic_atom,
  'smartphone': Ic_smartphone,
  'monitor': Ic_monitor,
  'camera': Ic_camera,
  'music-note': Ic_music_note,
};

export const DIAGRAM_ICON_KEYS: string[] = [
  'document-text', 'notebook', 'clipboard-text', 'folder', 'folder-with-files', 'widget-2',
  'layers-minimalistic', 'box', 'diagram-up', 'routing', 'server-2', 'database',
  'cloud', 'cpu', 'ssd-square', 'wi-fi-router', 'code-square', 'command',
  'programming', 'bug', 'home-2', 'buildings-2', 'city', 'shop',
  'cart-large-2', 'wallet', 'card', 'chart-2', 'pie-chart-2', 'graph-up',
  'user-circle', 'users-group-rounded', 'chat-round', 'bell', 'calendar', 'clock-circle',
  'settings', 'shield-check', 'lock', 'key', 'global', 'map-point',
  'target', 'compass', 'rocket', 'bolt', 'fire', 'magic-stick-3',
  'lightbulb', 'star', 'heart', 'bookmark', 'flag', 'crown',
  'gift', 'palette', 'pen-2', 'atom', 'smartphone', 'monitor',
  'camera', 'music-note',
];

/** Preset colors for the icon, Notion/Linear-style. Fixed hex so they read the same in both themes. */
export const DIAGRAM_ICON_COLORS: string[] = [
  '#64748b', '#ef4444', '#f97316', '#f59e0b', '#22c55e', '#14b8a6',
  '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e',
];

export const DEFAULT_DIAGRAM_ICON = 'document-text';
export const DEFAULT_DIAGRAM_ICON_COLOR = '#6366f1';

/** Render a diagram icon by key, tinted by color. Falls back to the default icon for unknown keys. */
export function DiagramIcon({ name, color, className }: { name?: string | null; color?: string | null; className?: string }) {
  const Comp = (name && DIAGRAM_ICON_MAP[name]) || DIAGRAM_ICON_MAP[DEFAULT_DIAGRAM_ICON];
  return <Comp className={className} style={{ color: color || DEFAULT_DIAGRAM_ICON_COLOR }} />;
}
